import { useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { StatCard } from '../components/ui';
import { useSettings } from '../hooks/useSettings';
import { useLedgerData } from '../hooks/useLedgerData';
import { DateRangeButton } from '../components/HeaderControls';
import { fmtMoney } from '../utils/currency';
import { profitAndLoss, computeDateWindow, type PLLine, type DateFilterKey } from '../utils/ledgerReports';

// ── Icons ─────────────────────────────────────────────────────────────────────
function IconRevenue({ size = 13 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <path d="M1.5 10.5l3.5-4 2.5 2.5 4.5-5.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 3h3v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconCost({ size = 13 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <rect x="1" y="3.5" width="12" height="7" rx="1.3" stroke="currentColor" strokeWidth="1.3" />
    <circle cx="7" cy="7" r="1.6" stroke="currentColor" strokeWidth="1.2" />
  </svg>;
}
function IconProfitIcon({ size = 13 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.3" />
    <path d="M7 4v6M8.7 5.3c0-.7-.76-1.3-1.7-1.3-.94 0-1.7.6-1.7 1.3 0 .7.76 1 1.7 1.15.94.15 1.7.45 1.7 1.15 0 .7-.76 1.3-1.7 1.3-.94 0-1.7-.6-1.7-1.3"
      stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}

function StatementBlock({ title, lines, total, money, negative, ledgerHref }: {
  title: string; lines: PLLine[]; total: number; money: (v: number) => string; negative?: boolean;
  /** Link to the General Ledger for an account (opens in a new tab); left out, lines are plain text. */
  ledgerHref?: (accountId: string) => string;
}) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: 'var(--text-3)',
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6,
      }}>{title}</div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
        {lines.length === 0 && (
          <div style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-3)' }}>Nothing in this period.</div>
        )}
        {lines.map(l => {
          const inner = (<>
            <span className="pl-name">
              {l.name}
              {ledgerHref && (
                <svg className="pl-go" width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path d="M7 1.5h3.5V5M10.5 1.5L5.5 6.5M9 7.5v2.5a.5.5 0 01-.5.5h-6.5a.5.5 0 01-.5-.5V3.5a.5.5 0 01.5-.5H5"
                    stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
            <span style={{ fontFamily: 'var(--font-num)', color: 'var(--text-1)' }}>
              {negative ? '(' : ''}{money(l.amount)}{negative ? ')' : ''}
            </span>
          </>);
          // Each account opens its General Ledger (same dates) in a new tab.
          return ledgerHref ? (
            <a key={l.accountId} className="pl-line pl-link" href={ledgerHref(l.accountId)} target="_blank" rel="noopener noreferrer"
              title={`Open ${l.name} in the General Ledger`}>{inner}</a>
          ) : (
            <div key={l.accountId} className="pl-line">{inner}</div>
          );
        })}
        <div style={{
          display: 'flex', justifyContent: 'space-between', padding: '10px 14px',
          background: 'var(--surface-2)', fontSize: 12, fontWeight: 700,
        }}>
          <span>Total {title}</span>
          <span style={{ fontFamily: 'var(--font-num)' }}>{negative ? '(' : ''}{money(total)}{negative ? ')' : ''}</span>
        </div>
      </div>
    </div>
  );
}

export default function ProfitPage() {
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);
  const { entries, accounts, loading } = useLedgerData();
  const { user } = useAuth();

  const [dateKey, setDateKey]       = useState<DateFilterKey>('this-month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo,   setCustomTo]   = useState('');

  const window = useMemo(() => computeDateWindow(dateKey, customFrom, customTo), [dateKey, customFrom, customTo]);
  const pl = useMemo(
    () => (window ? profitAndLoss(entries, accounts, window) : null),
    [entries, accounts, window],
  );
  // The General Ledger is owner only, so only owners get the links.
  const ledgerHref = user?.role === 'owner'
    ? (accountId: string) => {
        const q = new URLSearchParams({ account: accountId, range: dateKey });
        if (dateKey === 'custom') { q.set('from', customFrom); q.set('to', customTo); }
        return `/accounting/ledger?${q.toString()}`;
      }
    : undefined;

  return (
    <AppLayout
      title="Profit &amp; Loss"
      subtitle="Money in, money out, and what's left — straight from the books"
      toolbar={<>
        <DateRangeButton
          value={dateKey} onChange={setDateKey}
          customFrom={customFrom} customTo={customTo}
          onCustomFrom={setCustomFrom} onCustomTo={setCustomTo}
        />
      </>}
    >
      {loading ? (
        <div className="card"><div className="skeleton" style={{ height: 240, borderRadius: 8 }} /></div>
      ) : !window ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px', fontSize: 13, color: 'var(--text-3)' }}>
          Choose a From and To date above.
        </div>
      ) : !pl || (pl.income.length === 0 && pl.expense.length === 0) ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>Nothing in this period</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
            Record sales and expenses — or run <strong>Settings → Accounting → Rebuild Now</strong>.
          </div>
        </div>
      ) : (
        <>
          <div className="stat-row">
            <StatCard icon={<IconRevenue />} label="Money In" value={money(pl.totalIncome)} />
            <StatCard icon={<IconCost />} label="Money Out" value={money(pl.totalExpense)} />
            <StatCard
              icon={<IconProfitIcon />} label="Profit" value={money(pl.netProfit)}
              valueColor={pl.netProfit >= 0 ? 'var(--success)' : 'var(--danger)'}
              iconBg={pl.netProfit >= 0 ? '#f0fdf4' : '#fef2f2'}
              iconColor={pl.netProfit >= 0 ? 'var(--success)' : 'var(--danger)'}
            />
          </div>

          <div className="card">
            <StatementBlock title="Income" lines={pl.income} total={pl.totalIncome} money={money} ledgerHref={ledgerHref} />
            <StatementBlock title="Expenses" lines={pl.expense} total={pl.totalExpense} money={money} negative ledgerHref={ledgerHref} />
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 16px', borderRadius: 'var(--r-sm)',
              background: pl.netProfit >= 0 ? 'var(--success-bg)' : 'var(--danger-bg)',
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>Net Profit</span>
              <span style={{
                fontFamily: 'var(--font-num)', fontSize: 18, fontWeight: 800,
                color: pl.netProfit >= 0 ? 'var(--success)' : 'var(--danger)',
              }}>{money(pl.netProfit)}</span>
            </div>
          </div>
        </>
      )}
    </AppLayout>
  );
}
